// Pure helpers for the brand-settings social-accounts table. Kept in a
// separate module (vs. inlined in the JSX) so the status-pill, the (?)
// reason tooltip, and the Connect/Disconnect/Retry button logic can be
// unit-tested without rendering the heavy MDBReact table.
//
// Feature: when a connected account hits a token / connection problem,
// the worker flips its status to 'failed' and records a human-readable
// `statusReason`. This table surfaces that reason in a (?) tooltip next
// to the status and switches the action button to "Retry".

// statusReason may be a plain string OR a structured object the worker wrote
// (e.g. { provider, message } from tokenService / socialAccountHealth). Coerce
// to readable text so the (?) tooltip never renders the literal "[object
// Object]". Prefer the human message; fall back to other common fields, then a
// compact JSON dump.
export function reasonText(reason) {
  if (!reason) return '';
  if (typeof reason === 'string') return reason.trim();
  if (typeof reason === 'object') {
    const m = reason.message || reason.error || reason.reason || reason.detail || reason.description;
    if (m && typeof m === 'string') return m.trim();
    try { const j = JSON.stringify(reason); return j && j !== '{}' ? j : ''; } catch (_) { return ''; }
  }
  return String(reason).trim();
}

// Statuses that represent a problem the owner can fix by reconnecting.
export const PROBLEM_STATUSES = ['failed', 'expired'];

export function isProblemStatus(status) {
  return PROBLEM_STATUSES.indexOf(String(status || '').toLowerCase()) !== -1;
}

// Show the (?) reason icon only when the account is in a problem state.
// We still show it (with a generic tooltip) when no reason string was
// recorded, so the user always has a hint that a reconnect is needed.
export function shouldShowStatusReason(status, statusReason) {
  if (reasonText(statusReason)) return true;
  return isProblemStatus(status);
}

// Tooltip copy for the (?) icon. Prefer the recorded reason; fall back
// to a generic line for a problem status with no reason.
export function statusReasonTooltip(status, statusReason) {
  const txt = reasonText(statusReason);
  if (txt) return txt;
  if (isProblemStatus(status)) return 'This account needs to be reconnected to keep posting.';
  return '';
}

// Connect / Disconnect / Retry button descriptor for a given status.
// `action` is what onClick should do: 'disconnect' or 'connect' (a
// retry just re-runs the connect/OAuth flow).
export function connectButtonFor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'connected') return { label: 'Disconnect', color: 'danger', action: 'disconnect' };
  if (isProblemStatus(s)) return { label: 'Retry', color: 'warning', action: 'connect' };
  return { label: 'Connect', color: 'light-blue', action: 'connect' };
}

// Pull a statusReason out of the account doc(s) that back a platform
// row. Prefer the selected account; otherwise the first non-connected
// account that carries a reason.
export function pickStatusReason(selectedAccountDoc, accounts) {
  if (selectedAccountDoc && selectedAccountDoc.statusReason) {
    return reasonText(selectedAccountDoc.statusReason);
  }
  const list = Array.isArray(accounts) ? accounts : [];
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    if (a && a.statusReason && String(a.status || '').toLowerCase() !== 'connected') {
      return reasonText(a.statusReason);
    }
  }
  return null;
}
